import { useNavigate } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { ArrowLeft, ShieldCheck } from "lucide-react";
import { Button } from "@/shared/ui/button.jsx";
import { Spinner } from "@/shared/ui/spinner.jsx";
import { useAuth } from "@/features/auth/auth-context.jsx";
import { masterApi } from "@/api/endpoints/master.js";
import { toast } from "@/shared/ui/toaster.jsx";

/**
 * Faixa exibida enquanto um usuário Master está dentro do ambiente de um
 * cliente.
 *
 * A visita é uma propriedade da sessão no servidor, e sair dela é uma
 * chamada de API que fica registrada na auditoria. No app antigo isso era
 * uma chave no `localStorage`: qualquer pessoa podia criar a chave à mão e
 * o sistema exibia o banner como se fosse um Master de verdade.
 */
export function ImpersonationBanner() {
  const { impersonating, refresh } = useAuth();
  const navigate = useNavigate();

  const sair = useMutation({
    mutationFn: masterApi.stopImpersonation,
    onSuccess: async () => {
      await refresh();
      navigate("/master/clientes", { replace: true });
      toast.success("Você voltou ao painel Master.");
    },
    onError: (erro) => toast.error(erro.message),
  });

  if (!impersonating) return null;

  return (
    <div className="sticky top-0 z-40 flex flex-wrap items-center justify-between gap-2 border-b border-warning/40 bg-warning/10 px-4 py-2 text-xs">
      <div className="flex items-center gap-2">
        <ShieldCheck className="h-4 w-4 shrink-0" />
        <span>
          Você está acessando o ambiente de <strong>{impersonating.clinicName}</strong> como Usuário
          Master.
        </span>
      </div>
      <Button
        size="sm"
        variant="outline"
        className="h-7 gap-1 border-warning/50 bg-background/60"
        onClick={() => sair.mutate()}
        disabled={sair.isPending}
      >
        {sair.isPending ? <Spinner className="h-3.5 w-3.5" /> : <ArrowLeft className="h-3.5 w-3.5" />}
        Voltar ao painel Master
      </Button>
    </div>
  );
}
